import CheckoutError from '@/lib/Checkout/CheckoutError';
import ErrorLevel from '@/lib/Errors/ErrorLevel';
import LangString from '@/lib/Lang/LangString';
import UserCancelledPaymentError from '@/lib/Checkout/PaymentCancelledError';
import UserFacingError from '@/libraries/DigiTickets/Errors/UserFacingError';
import { addGreekTaxToOrder } from '@/lib/Tax/Countries/Greece';
import { CartLineDetailsTabNameEnum } from '@/epos/vue/modals/CartLineDetails/CartLineDetailsTabNameEnum';
import { combineDuplicateLines } from '@/lib/Cart/combineDuplicateLines';
import { generateStockLevelMessage } from '@/libraries/DigiTickets/StockLevelHelper';
import { handleGroupBookingReferenceEdit } from '@/lib/GroupBookings';
import { isElementVisible } from '@/functions/dom';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';
import { toYMD } from '@/functions/date';
import { updateAngularUi } from '@/functions/angular';

const _ = require('lodash');
const CartLine = require('@/lib/Cart/CartLine');
const ChangeCalculator = require('@/libraries/DigiTickets/Payment/ChangeCalculator');
const CustomerDetailsCtrl = require('@/controllers/modal/CustomerDetailsCtrl');
const CustomerScreenStages = require('@/lib/CustomerScreen/CustomerScreenStages');
const DateFormat = require('@/lib/Dates/DateFormat');
const EmbeddedComponents = require('@/vue/EmbeddedComponents');
const EventNames = require('@/lib/Events/EventNames');
const FieldInstanceFilter = require('@/lib/CustomFields/FieldInstanceFilter');
const GiftVoucherDeliveryTypeEnum = require('@/lib/GiftVouchers/GiftVoucherDeliveryTypeEnum');
const ItemEditableStatus = require('@/lib/Orders/ItemEditableStatus');
const ItemRequest = require('@/lib/Items/ItemRequest');
const ItemType = require('@/lib/Items/ItemType');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const MembershipDataset = require('@/lib/Memberships/MembershipDataset');
const moment = require('moment');
const OrderValidationError = require('@/lib/Validation/OrderValidationError');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PaymentSaveError = require('@/lib/Payments/PaymentSaveError');
const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');
const SellState = require('@/lib/Cart/SellState');
const SoldGiftVoucherData = require('@/lib/GiftVouchers/SoldGiftVoucherData');
const TaxReceiptUnavailableReasonEnum = require('@/lib/Tax/TaxReceiptUnavailableReasonEnum');
const TradingSessionEvent = require('@/lib/TradingSessions/TradingSessionEvent');
const TradingSessionEventType = require('@/lib/TradingSessions/TradingSessionEventType');
const { CartLineUiState } = require('@/lib/Cart/calculateCartLineUiState');
const { clearOrderSearchCache } = require('@/lib/Orders/orderSearchCacheFuncs');
const { cloneDeep, cloneShallow } = require('@/functions/clone');
const { countSessionSpacesInGroup, getTicketQtysInGroup } = require('@/libraries/DigiTickets/Cart/cartItemGroups');
const { ModalCancelledError } = require('@/vue/modals/ModalCancelledError');
const { setDeviceTradingSessionIfMissing } = require('@/lib/Devices');
const { setFieldValuesOnCart } = require('@/lib/CustomFields/fieldFunctions');
const { timeoutPromise } = require('@/functions/functions');

/**
 * @typedef {import('@/lib/DeviceGroups/DeviceGroup')} DeviceGroup
 */

/**
 * @param $filter
 * @param $location
 * @param $modal
 * @param $rootScope
 * @param $scope
 * @param $timeout
 * @param {AppConfig} appConfig
 * @param {CartService} cartService
 * @param {CartStasher} cartStasher
 * @param {CartToOrderMapper} cartToOrderMapper
 * @param {CashDrawerService} cashDrawerService
 * @param {CheckoutService} checkoutService
 * @param {CurrentDevice} currentDevice
 * @param {CustomerScreenDataService} customerScreenDataService
 * @param {DialogService} dialogService
 * @param {FieldRepository} fieldRepository
 * @param {GiftAidPrompter} giftAidPrompter
 * @param {ItemBarcodeService} itemBarcodeService
 * @param {ItemOptionManager} itemOptionManager
 * @param {ItemRepository} itemRepository
 * @param {KitchenReceiptPrinter} kitchenReceiptPrinter
 * @param {LangService} langService
 * @param {MembershipCancellationRunner} membershipCancellationRunner
 * @param {MembershipManagementService} membershipManagementService
 * @param {MembershipService} membershipService
 * @param {ModalFactory} modalFactory
 * @param {NavigationService} navigationService
 * @param {OfferRepository} offerRepository
 * @param {OrderInProgressStasher} orderInProgressStasher
 * @param {OrderReceiptPrinter} orderReceiptPrinter
 * @param {OrderValidator} orderValidator
 * @param {PaymentService} paymentService
 * @param {PrivilegesManager} privilegesManager
 * @param {ProductRepository} productRepository
 * @param {SellService} sellService
 * @param {SessionEventRepository} sessionEventRepository
 * @param {SessionRepository} sessionRepository
 * @param {TaxManager} taxManager
 * @param {TicketRepository} ticketRepository
 * @param {ToastFactory} toastFactory
 * @param {TradingSessionManager} tradingSessionManager
 * @param {UserService} userService
 * @param {VueModalFactory} vueModalFactory
 */
export const SellCtrl = function (
    $filter,
    $location,
    $modal,
    $rootScope,
    $scope,
    $timeout,
    appConfig,
    cartService,
    cartStasher,
    cartToOrderMapper,
    cashDrawerService,
    checkoutService,
    currentDevice,
    customerScreenDataService,
    dialogService,
    fieldRepository,
    giftAidPrompter,
    itemBarcodeService,
    itemOptionManager,
    itemRepository,
    kitchenReceiptPrinter,
    langService,
    membershipCancellationRunner,
    membershipManagementService,
    membershipService,
    modalFactory,
    navigationService,
    offerRepository,
    orderInProgressStasher,
    orderReceiptPrinter,
    orderValidator,
    paymentService,
    privilegesManager,
    productRepository,
    sellService,
    sessionEventRepository,
    sessionRepository,
    taxManager,
    ticketRepository,
    toastFactory,
    tradingSessionManager,
    userService,
    vueModalFactory,
) {
    const logger = logFactory('SellCtrl');

    navigationService.showNav();

    // Early exit if no access permission
    privilegesManager.requirePrivilegeOr403(PrivilegeNames.ACCESS_SELL);

    $timeout(() => {
        // Vue <EposAppRoot> watches for this to enable the teleported Vue <SellView>.
        window.rootVueStore.commit('addActiveEmbeddedComponent', { name: EmbeddedComponents.SELL_VIEW });
    });

    // Vue <EposAppRoot> watches for this to disable the teleported Vue <SellView>.
    $scope.$on('$destroy', () => {
        window.rootVueStore.commit('removeActiveEmbeddedComponent', EmbeddedComponents.SELL_VIEW);
    });

    $scope.appConfig = appConfig;
    $scope.user = userService;

    /**
     * @type {MembershipCancellationRunner}
     */
    $scope.membershipCancellationRunner = membershipCancellationRunner;

    /**
     * FieldInstanceFilter can be used in the template in future if we want to display product options
     * on cart lines.
     * For now it's just useful if you want to do some debugging in the template but isn't actually used.
     *
     * @type {ReadonlyArray<any>}
     */
    $scope.FieldInstanceFilter = FieldInstanceFilter;

    /**
     * Make the item type "constants" available to the template.
     *
     * @type {ItemType}
     */
    $scope.itemType = ItemType;

    $scope.state = 'cart';

    /**
     * Incorrectly named property. Don't use this, use $scope.cartService.
     *
     * @type {CartService}
     */
    $scope.cart = cartService;

    /**
     * Correctly named property. Some day we'll get rid of $scope.cart so it's not confused with a Cart model.
     *
     * @type {CartService}
     */
    $scope.cartService = cartService;

    $scope.CartLineUiState = CartLineUiState;
    $scope.payProcessing = false;
    $scope.paymentInProgress = false;
    $scope.editingCartItem = null;
    $scope.ItemEditableStatus = ItemEditableStatus;
    $scope.hasFood = false;
    $scope.hasMembershipPlans = false;
    $scope.toastFactory = toastFactory;
    $scope.shouldShowChangeModal = true;

    /**
     * The category of the last ticket selected in the modal. We store this so that if the operator decides to
     * add more tickets the modal opens in the last category they added a ticket from.
     *
     * @type {?Category}
     */
    $scope.lastTicketSelectedCategory = null;

    /**
     * Display the offers button?
     * It will be hidden if there are no offers that exist.
     *
     * @type {boolean}
     */
    $scope.showOffersButton = window.rootVueStore.state.cartOffers.manualOrderOffersCount > 0;

    /**
     * Display the fields button?
     * It will be hidden if there are no fields that exist.
     * The button can also be visible but disabled if there are fields that exist, but nothing
     * is applicable to the current cart.
     *
     * @type {boolean}
     */
    $scope.showFieldsButton = window.rootVueStore.state.sell.fieldsCount > 0;

    /**
     * @type {boolean}
     */
    $scope.deviceCanManageGroupBookings = currentDevice.device.manageGroupBookings;

    customerScreenDataService.setStage(CustomerScreenStages.WAIT);

    // TODO: This should not happen now as there is new logic to prevent getting here without a user and device.
    // Ensure that is the case then remove this check.
    if (!currentDevice.isSet()) {
        $location.path('/device');
    }

    $scope.canPay = function canPay() {
        if ($scope.paymentInProgress) {
            return false;
        }

        return !cartService.isEmpty();
    };

    /**
     * @param {string} barcode
     */
    $scope.barcodeScanHandler = async function (barcode) {
        if ($scope.paymentInProgress) {
            // only allow scanning when we're not in the middle of a payment
            return;
        }

        let searchingToast = toastFactory.spinner('SEARCHING');

        // Try finding a product with that barcode.
        const item = await itemBarcodeService.findItemByBarcode(barcode);
        if (item) {
            console.debug(`Found item with barcode ${barcode}:`, item);
            // Got an item - add it to the cart.
            $scope.addItem(item, 1);
            toastFactory.clear(searchingToast);
            // Return so that we don't continue to search for other things with this barcode (memberships).
            return;
        }

        // No product found with that barcode.
        console.warn('No items found with barcode:', barcode);
        // Continue to look for other things with this barcode...

        // Try finding a membership with that barcode.
        membershipService.getMembershipByRef(barcode)
            .then((membership) => {
                $timeout(() => {
                    // Set cart membership, and show notification
                    $scope.cart.customer.setMembership(membership);

                    if (membership.customerAccount !== null) {
                        $scope.cart.customer.setAccount(membership.customerAccount);
                    } else if (membership.members.length > 0 && membership.members[0].contact) {
                        $scope.cart.customer.setContact(membership.members[0].contact);
                    }

                    cartService.recalculateAfterModifyingCartContents().then(() => {});

                    toastFactory.clear(searchingToast);
                });
            })
            .catch(() => {
                $timeout(() => {
                    // Show not found notification
                    toastFactory.clear(searchingToast);
                    toastFactory.warningTop(null, "Sorry, we couldn't find this product, membership, or customer.");
                });
            });
    };
    $scope.debouncedBarcodeScanHandler = _.debounce($scope.barcodeScanHandler, 100);

    // Add scanned items to the cart.
    $scope.$on(EventNames.BARCODE_SCANNED, function (event, response) {
        if (response.hasOpenModal) {
            // Don't do anything on this page if a modal is open on top of it.
            return;
        }

        $scope.debouncedBarcodeScanHandler(response.code);
    });

    $scope.clearCart = async () => {
        $scope.clearPayment();
        await cartService.clear();
        $scope.clearProcessing();
    };

    $scope.clearCartButtonPressed = async function clearCartButtonPressed() {
        // The default behavior is to clear the cart but when editing an order we need to check if the cart has changed.
        let clearCart = true;

        const clearCartConfirmationEditedOrder = () => {
            return dialogService.confirm(
                'SELL.CART_CLEAR_WARNING',
                {
                    cancelLabel: 'GO_BACK',
                    okLabel: 'DIALOGS.DISCARD_CHANGES',
                    cancelColor: 'default',
                    okColor: 'danger',
                }
            );
        };

        if ($scope.cart.currentCart.originalOrder && $scope.cart.currentCart.contentHashChanged()) {
            const confirmed = await clearCartConfirmationEditedOrder();
            if (!confirmed) {
                clearCart = false;
            }
        }

        if (clearCart) {
            dialogService
                .confirm('CART_PREVIEW.CONFIRM_CLEAR_CART')
                .then((confirmed) => {
                    if (!confirmed) {
                        return;
                    }
                    $timeout(() => {
                        $scope.redirectWhenDone(cartService.currentCart, false, true);
                        $scope.clearCart();
                    });
                });
        }
    };

    $scope.clearCartButtonText = function clearCartButtonText() {
        if ($scope.getCurrentSellState().cancelCartReturnPath) {
            // If clicking the clear button will take the user back where they came from, display it
            // as a cancel button instead of clear.
            return 'SELL.CANCEL';
        }

        return 'SELL.CLEAR';
    };

    /**
     * This is similar to clearing the cart, but it does not simply dump the cart and start fresh. Instead, it sets each
     * line in the cart to 0 qty but leaves the rest of the data intact. This is for cancelling an order while editing.
     */
    $scope.zeroQuantityAllLines = () => {
        // Check for already redeemed lined that cannot be set to zero.
        const redeemedLines = cartService.currentCart.itemList.filter((cartLine) => {
            return cartLine.redeemed > 0;
        });

        let customHtml = '';
        if (redeemedLines.length > 0) {
            customHtml = '<div class="alert alert-warning text-base-size">'
                + '<strong>'
                + '<i class="fa-solid fa-exclamation-triangle"></i> '
                + langService.getText('SELL.REMOVE_ALL_LINES__REDEEMED')
                + '</strong><br/>'
                + redeemedLines.map((cartLine) => cartLine.redeemed + 'x ' + cartLine.getName()).join('<br/>')
                + '</div>';
        }

        dialogService
            .confirm(
                'SELL.REMOVE_ALL_LINES__CONFIRMATION',
                {
                    customHtml,
                }
            )
            .then((confirmed) => {
                if (confirmed) {
                    cartService.zeroQuantityAllLines();
                }
            });
    };

    /**
     * Add an item to the cart.
     *
     * @param {AbstractItem} item
     * @param {Number} quantity
     * @param {?object} [extraInfo]
     * @param {?boolean} triggeredByItemButton - was this action triggered by the item button in the
     *     right panel on the sell screen?
     *
     * @return {Promise<void>}
     */
    $scope.addItem = async (item, quantity, extraInfo, triggeredByItemButton = false) => {
        console.debug('Add item', quantity, item, extraInfo, triggeredByItemButton);

        if ($scope.getCurrentSellState().preventAdditional) {
            if (typeof $scope.getCurrentSellState().preventAdditional === 'string') {
                toastFactory.warningTop($scope.getCurrentSellState().preventAdditional);
            }
            return;
        }

        // Make sure the operator doesn't try to sell memberships that are past their allowed usage date.
        if (item.itemType === ItemType.MEMBERSHIP_PLAN) {
            if (item.hasOwnProperty('allowedUsageToDate') && item.allowedUsageToDate !== null) {
                if (item.allowedUsageToDate < (new Date())) {
                    toastFactory.warningTop('SELL.CANNOT_SELL_MEMBERSHIP_BEYOND_ALLOWED_USAGE_DATES');
                    return;
                }
            }
        }

        let lineBranch = sellService.getBranchForItem(item);

        let cartItem = new CartLine(
            $scope.cartService,
            item,
            lineBranch,
        );

        // Triggers cartService.calculateCart() where stock level errors are being calculated.
        cartItem.setQuantity(quantity);

        if (cartItem.item.allowPriceEdit && cartItem.customPrice === null) {
            try {
                await showPriceEditModal(cartItem);
            } catch (e) {
                console.warn('Price entry cancelled. Cancelling add to cart.', e);
                return;
            }
        }

        // Check recurring payments (or different patterns) and single payments are not being mixed.
        // By this point both Gift Aid and a custom price have already been chosen, so we know if the price
        // of the item will be zero.
        const currentPaymentPattern = cartService.currentCart.getCurrentPaymentPattern();
        if (cartItem.isPositiveValue()
            && currentPaymentPattern
            && item.paymentPattern
            && item.paymentPattern !== currentPaymentPattern
        ) {
            showMixedPaymentPatternError();
            return;
        }

        if (item.itemType === ItemType.GIFT_VOUCHER) {
            // Adding a gift voucher.

            // Gift vouchers can only have 1 instance per line.
            cartItem.setCanAdjustQuantity(false);

            // Create a new soldGiftVoucherData for a new item.
            cartItem.soldGiftVoucherData = new SoldGiftVoucherData();

            // Default to in person delivery.
            cartItem.soldGiftVoucherData.deliveryType = GiftVoucherDeliveryTypeEnum.IN_PERSON;

            try {
                await $scope.showSoldGiftVoucherDataModal('add-new', cartItem);
            } catch (e) {
                console.warn('Gift Voucher data entry cancelled. Cancelling add to cart.', e);
                return;
            }
        } else if (item.isSessionBased()) {
            // Check if a session was passed in. This should be present in extraInfo when adding a session-based ticket.
            if (extraInfo && extraInfo.session) {
                cartItem.session = extraInfo.session;
            }

            if (extraInfo && extraInfo.groupBookingReferenceData) {
                // If the action is a rename we need to update all lines in the cart that used the old ref.
                if (extraInfo.groupBookingReferenceData.type === 'rename') {
                    await cartService.updateGroupBookingReference(
                        extraInfo.groupBookingReferenceData.originalReference,
                        extraInfo.groupBookingReferenceData.newReference
                    );
                }

                // Now for both renames and new references we just set the ref on the new cart item.
                cartItem.groupBookingReference = extraInfo.groupBookingReferenceData.newReference;

                if (cartItem.groupBookingReference) {
                    // If they have added a reference this must be a group booking.
                    cartService.currentCart.groupBooking = true;
                }
            }
        }

        // Remember the chosen session for this event so it can be
        // pre-selected when adding more tickets for this event.
        if (cartItem.session !== null) {
            cartService.currentCart.eventSessionPreferences.setPreferredSessionForEvent(
                cartItem.session.eventID,
                cartItem.session
            );
        }

        // We no longer ask the Gift Aid question here because we need to show the total donation
        // amount to the customer when they are confirming the declaration.

        // Memberships need an account.
        if (cartItem.item.itemType === ItemType.MEMBERSHIP_PLAN) {
            // Membership plans can only have 1 instance per line.
            cartItem.setCanAdjustQuantity(false);

            // Ensure we have a customer account before even adding to the cart.
            try {
                await $scope.ensureHaveCustomerAccount();
            } catch (e) {
                console.warn('Selecting a customer account was cancelled. Cannot continue adding membership.');
                return;
            }
        }

        // Load options for this item.
        let optionSets = [];
        if (triggeredByItemButton) {
            optionSets = await itemOptionManager.findOptionSetsForItem(item.ID);
        }

        // From this point on the item is actually in the cart, so cancelling beyond here needs to
        // remove it from the cart.
        cartItem = await cartService.addNewLineOrAdjustQuantityOfExisting(
            cartItem,
            triggeredByItemButton,
            optionSets
        );

        // Display membership data modal for the new line (or new instance on exiting line).
        // This happens after the line is added to the cart (cart.add) because if we are adding an
        // instance of a plan that is already in the cart the modal should show tabs for the existing instances.
        // cart.add will return the existing CartItem in the cart that has those instances, and that's what
        // whose instances we want to display and add the new instance to, not a new CartItem.
        if (cartItem.item.itemType === ItemType.MEMBERSHIP_PLAN) {
            if (cartItem.membershipDatasets.length < cartItem.quantity) {
                try {
                    const membershipDatasets = await $scope.displayNewMembershipDatasetModal(cartItem);
                    await cartService.modifyCurrentCart(() => {
                        cartItem.setMembershipDatasets(membershipDatasets);
                    });
                } catch (e) {
                    console.warn('Membership data entry cancelled. Cancelling add to cart.', e);
                    // Remove the qty that was just added.
                    await cartService.adjustCartLineQuantity(cartItem, -quantity);
                    return;
                }
            }
        }

        // If there are 'askEarly' fields, then go and ask for them.
        // The addNewLineOrAdjustQuantityOfExisting call above will have caused the fields on the cart
        // and the item instances in the cart to be recalculated, so we can just show what is now there.
        if (
            optionSets.length > 0
            || (cartItem.itemInstances.length === 1 && (!cartItem.item.isSessionBased() || triggeredByItemButton))
        ) {
            let askEarlyInstances = cartItem.itemInstances[0].fields.instances.filter(
                FieldInstanceFilter.ASK_EARLY_FIELDS_ONLY
            );
            console.log(`There are ${askEarlyInstances.length} ask early fields for this item.`);

            if (askEarlyInstances.length > 0 || optionSets.length > 0) {
                // For the first instance show the modal to answer the fields.
                $scope.showItemEditModal(
                    cartItem,
                    CartLineDetailsTabNameEnum.FIELDS,
                    triggeredByItemButton,
                    'SELL.CANCEL_ADDING'
                );
            }
        }
        // For additional instances the values will be copied from the first instance on the line.

        // If Gift Aid is enabled but not enough customer info has been added show the customer details
        // modal to allow it to be entered now.
        if (cartService.giftAidEnabled() && !cartService.currentCart.customer.hasGiftAidData()) {
            $scope.showCustomerDetails(
                { langKey: 'SELL.GIFT_AID_CUSTOMER_INFO_REQUIRED' },
                true
            );
        }

        /**
         * If the UI isn't already being updated, trigger an
         * update of the bindings. This is need where cart adding
         * is triggered via barcode scan input rather than mouse
         * events taking place in the UI.
         */
        $timeout(function () {
            $scope.$apply();

            // If the item we've just added is not visible then scroll to it.
            const element = document.getElementById(`cart-line-${cartItem.thirdPartyRef}`);
            const cartContainer = document.getElementById('cart');
            if (!element || !cartContainer) {
                return;
            }
            if (!isElementVisible(element, cartContainer)) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'end',
                });
            }

            // Temporarily highlight target item.
            const HIGHLIGHTED_CLASS = 'cart-item--highlighted';
            element.classList.add(HIGHLIGHTED_CLASS);
            setTimeout(() => {
                element.classList.remove(HIGHLIGHTED_CLASS);
            }, 1000);
        });
    };

    $scope.$on(EventNames.ADD_ITEM_TO_CART_REQUESTED, (e, data) => {
        $scope.addItem(
            data.item,
            data.quantity,
            data.extraInfo || null,
            !!data.triggeredByItemButton
        );
    });

    /**
     * Remove an item from the cart (completely, not just decrease qty) by clicking the delete icon on a line.
     *
     * @param {CartLine} cartItem
     */
    $scope.removeItem = (cartItem) => {
        if (cartItem.isMembershipPlan()) {
            dialogService
                .confirm(cartItem.quantity > 1 ? 'MEMBERSHIPS.CONFIRM_REMOVE_ALL' : 'MEMBERSHIPS.CONFIRM_REMOVE')
                .then((confirmed) => {
                    if (confirmed) {
                        $scope.cart.remove(cartItem);
                    }
                });
        } else {
            $scope.cart.remove(cartItem);
        }
    };

    $scope.setProcessing = function setProcessing() {
        $scope.payProcessing = true;
        $scope.state = 'processing';
    };

    $scope.clearProcessing = function clearProcessing() {
        $scope.payProcessing = false;
        $scope.state = 'cart';
    };

    /**
     * Order has been successfully completed or failed/cancelled.
     * Send the user (back to) where they should be.
     *
     * @param {Cart} cart
     * @param {boolean} wasSuccessful
     * @param {boolean} [cartWasCancelled]
     * @param {boolean} [paymentWasCancelled]
     */
    $scope.redirectWhenDone = (
        cart,
        wasSuccessful,
        cartWasCancelled = false,
        paymentWasCancelled = false
    ) => {
        let sellState = cart.sellState;
        $timeout((() => {
            if (wasSuccessful && sellState.successReturnPath) {
                $location
                    .path(sellState.successReturnPath.path)
                    .search(sellState.successReturnPath.params);
            } else if (cartWasCancelled && sellState.cancelCartReturnPath) {
                $location
                    .path(sellState.cancelCartReturnPath.path)
                    .search(sellState.cancelCartReturnPath.params);
            } else if (paymentWasCancelled && sellState.cancelPaymentReturnPath) {
                $location
                    .path(sellState.cancelPaymentReturnPath.path)
                    .search(sellState.cancelPaymentReturnPath.params);
                // Clear the cart otherwise we can't get back to an empty cart.
                $scope.clearCart();
            }
        }));
    };

    $scope.clearPayment = function clearPayment() {
        cartService.clearPayments();
        $scope.clearProcessing();
        $scope.paymentInProgress = false;
    };

    /**
     * The order has been completed, finish up.
     * - Display change on customer screen.
     * - Update available spaces for sessions.
     * - Log any voucher payments in trading session.
     * - Create tax line for Greece
     * - Print receipt (if enabled)
     * - Cancel any memberships that need cancelling
     * - Show the change modal
     * - Reset the screen for the next cart
     *
     * @param {CheckoutResult} checkoutResult
     */
    $scope.finishOrder = async function (checkoutResult) {
        const createdOrder = checkoutResult.order;
        const orderAdjustmentResponse = checkoutResult.orderAdjustmentResponse;

        let calculator = new ChangeCalculator();
        calculator.setAmountDue(cartService.currentCart.getTotalDue());
        calculator.setAmountTendered(cartService.getTotalTendered());

        customerScreenDataService.updateState((state) => {
            state.summary = {
                total: calculator.getAmountDue(),
                paid: calculator.getAmountTendered(),
                change: calculator.getChange(),
            };
            state.stage = CustomerScreenStages.COMPLETE;
        });

        /**
         * Update the local session availability, based on what we've just sold. The session refresher
         * task will come along in a couple of minutes anyway, and give a definitive availability, but
         * these quick adjustments mean that at least the sessions display a more correct availability
         * straight away.
         *
         * @type {Object<number>}
         */
        let sessionSpacesSold = {};

        createdOrder.items.forEach(function (orderLine) {
            if (orderLine.session) {
                if (!sessionSpacesSold.hasOwnProperty(orderLine.session.ID)) {
                    sessionSpacesSold[orderLine.session.ID] = 0;
                }
                let people = orderLine.people ? parseInt(orderLine.people, 10) : 1;
                sessionSpacesSold[orderLine.session.ID] += (orderLine.qty * people);
            }
        });

        // eslint-disable-next-line unused-imports/no-unused-vars
        for (let sessionID in sessionSpacesSold) {
            if (sessionSpacesSold.hasOwnProperty(sessionID)) {
                // Adjust the session to add the spaces we just sold and take them off the available spaces.
                sessionRepository.adjustSoldSpaces(
                    parseInt(sessionID, 10),
                    sessionSpacesSold[sessionID]
                ).catch(() => {});
            }
        }

        // Add payments to sales counter.
        let paymentsLog = [];
        for (let i = 0; i < createdOrder.payments.length; i++) {
            let payment = createdOrder.payments[i];
            paymentsLog.push(
                [
                    payment.paymentMethod ? payment.paymentMethod.ref : null,
                    payment.amount,
                ]
            );
            if (payment.paymentMethod && payment.paymentMethod.ref === PaymentMethodRef.VOUCHER) {
                tradingSessionManager.saveEvent(
                    (new TradingSessionEvent(TradingSessionEventType.VOUCHER))
                        .setAmount(payment.value)
                );
            }
        }

        // TODO: Remove this once Entersoft is used instead of Sign Pro.
        await addGreekTaxToOrder(createdOrder);

        logger.log(
            'Order complete',
            {
                bookingRef: createdOrder.bookingRef,
                payments: paymentsLog,
            }
        );

        // Add (non-Greek) tax info the order.
        let taxLoadError = null;
        try {
            if (orderAdjustmentResponse) {
                // For adjustment orders load the overall booking tax for the new shape of the order,
                // plus the tax specific to the adjustment order that was just made.
                await taxManager.addTaxToOrderAdjustment(orderAdjustmentResponse);
            } else {
                // For non-adjustment orders load the overall booking tax for the new order.
                // There will only be one order in the booking so the addOrderTaxToOrder method
                // would also do the same thing here.
                await taxManager.addBookingTaxToOrder(createdOrder);
            }
        } catch (err) {
            // There was an error loading the tax summary for the order.
            // A VAT receipt will not be available.
            console.error('Failed to load tax for order.', err);
            taxLoadError = err;
        }

        autoPrintReceiptIfEnabled(createdOrder, orderAdjustmentResponse);

        // Print a kitchen receipt for this order (if enabled):
        // - If the cart contains any kitchen items
        // - If the last version of the order contained any kitchen items (e.g. print a cancelled kitchen order receipt)
        // - If the cart is a restored Kitchen Order
        // - If the cart contents have changed
        if (
            currentDevice.device.autoPrintKitchenReceipts
            && cartService.currentCart.contentHashChanged()
        ) {
            if (orderAdjustmentResponse && (
                orderAdjustmentResponse.adjustedOrder.containsKitchenItems()
                || (
                    !orderAdjustmentResponse.adjustedOrder.containsKitchenItems()
                    && cartService.currentCart.originalOrder.containsKitchenItems()
                )
            )) {
                await kitchenReceiptPrinter.printOrder(
                    orderAdjustmentResponse.adjustedOrder,
                    cartService.currentCart.restoredKitchenOrder,
                    false
                );
            } else {
                if (createdOrder.containsKitchenItems()) {
                    await kitchenReceiptPrinter.printOrder(
                        createdOrder,
                        cartService.currentCart.restoredKitchenOrder,
                        false
                    );
                }
            }
        }

        if (orderAdjustmentResponse || cartService.currentCart.isOrderAdjustmentBalancePayment()) {
            // If this order was in the order search cache it will now be out of date so clear the cache.
            clearOrderSearchCache();
        }

        // Get a copy of the cart that was used to create this order. This contains the original order if this is
        // an order edit and the sellState used to redirect when closing the change modal.
        const currentCart = cloneDeep(cartService.currentCart);

        // Get a copy of any memberships that need to be cancelled.
        const cancellingMemberships = cartService.getCancellingMemberships();

        // Now clear the cart as we have a copy now to use in the change modal.
        await $scope.clearCart();

        let changeModalMessages = [];

        // Handle cancelling any memberships where refunds are being issued as those refunds
        // have been issued now that the order is complete.
        if (cancellingMemberships.length > 0) {
            $scope.setProcessing();
            // Wrap the cancel callback style function in a promise so we can await it.
            await new Promise((resolve) => {
                $scope.membershipCancellationRunner.cancel(
                    cancellingMemberships,
                    function (cancelledMemberships, uncancelledMemberships, messages) {
                        changeModalMessages = changeModalMessages.concat(messages);
                        resolve();
                    },
                );
            });
            $scope.clearProcessing();
        }

        $scope.showChange(
            checkoutResult,
            createdOrder,
            changeModalMessages,
            orderAdjustmentResponse,
            currentCart,
            taxLoadError,
        );
    };

    /**
     * @param {Order} createdOrder
     * @param {OrderAdjustmentResponse|null} [orderAdjustmentResponse]
     */
    const autoPrintReceiptIfEnabled = (createdOrder, orderAdjustmentResponse) => {
        if (!currentDevice.device.autoPrintReceipts()) {
            cashDrawerService.openCashDrawerWithoutPrinting();
            return;
        }

        let originalOrder = null;
        if ($scope.cart.originalOrder) {
            originalOrder = $scope.cart.originalOrder;
        }

        // A VAT receipt will be printed instead of a regular receipt if:
        // - The user enabled the Print VAT Receipt toggle in the payment modal.
        // - The order just created is >=£250
        // - The order just created was an edit, and the order being edited was ever >= £250
        const printTaxReceipt = orderReceiptPrinter.autoPrintTaxReceipts
            || taxManager.mustPrintTaxReceiptForOrder(userService.getCompany(), createdOrder)
            || (originalOrder && taxManager.mustPrintTaxReceiptForOrder(userService.getCompany(), originalOrder));

        if (printTaxReceipt && !createdOrder.hasDataForTaxReceipt()) {
            // If a VAT receipt is mandatory but a VAT receipt is unavailable display an alert and don't auto print.
            taxManager.showTaxReceiptUnavailableAlert(TaxReceiptUnavailableReasonEnum.OFFLINE_DURING_SALE);
            return;
        }

        console.log(printTaxReceipt ? 'Automatically printing tax receipt' : 'Automatically printing receipt');

        orderReceiptPrinter
            .printOrderReceipt(
                createdOrder,
                orderAdjustmentResponse,
                originalOrder,
                printTaxReceipt,
            )
            .finally(() => {
                $timeout((() => {
                    // Update the UI.
                }));
            });
    };

    /**
     * @param {CheckoutResult} checkoutResult
     * @param {Order} order
     * @param {Array<{type: string, text:string}>} [additionalMessages]
     * @param {OrderAdjustmentResponse} [orderAdjustmentResponse]
     * @param {Cart} cart The cart that was just sold. This is passed in because the currentCart may have
     *      already been reset by the time we get to here.
     * @param {Error|null} taxLoadError
     */
    $scope.showChange = function (
        checkoutResult,
        order,
        additionalMessages,
        orderAdjustmentResponse,
        cart,
        taxLoadError,
    ) {
        if ($scope.shouldShowChangeModal) {
            console.log('Showing Change Modal');

            vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.CHANGE,
                    {
                        additionalMessages,
                        cart,
                        checkoutResult,
                        order,
                        orderAdjustmentResponse,
                        taxLoadError,
                    }
                )
                .then(({ destination }) => {
                    // OK button was pressed in the change modal.
                    customerScreenDataService.setStage(CustomerScreenStages.WAIT);

                    if (destination === 'view-order') {
                        // Send the user to view the order just created/edited.
                        navigationService.viewOrderDetails(
                            order.getOriginalBookingRef(),
                            null,
                            cart.sellState.viewOrderButtonPathQuery
                        );
                    } else if (destination === 'new-order') {
                        // Clear the cart and allow creating a new order.
                        // The cart is already cleared at this point so we can do nothing.
                    } else {
                        $scope.redirectWhenDone(cart, true);
                    }
                });
        } else {
            console.log('Skip Change Modal');
            // We aren't showing the change modal but we still need to redirect.
            $scope.redirectWhenDone(cart, true);
        }
        // Reset the flag.
        $scope.shouldShowChangeModal = true;
    };

    $scope.disableGiftAid = () => {
        cartService.disableGiftAid();
    };

    $scope.enableGiftAidPricesOnly = () => {
        cartService.enableGiftAidPricesOnly();
    };

    $scope.reAskGiftAid = () => {
        cartService.clearGiftAidChoice();
        return giftAidPrompter.prompt(cartService);
    };

    $scope.calculatePriceIncreaseForGiftAid = () => {
        return giftAidPrompter.calculateAdditionalGiftAidDonationForCart(cartService.currentCart);
    };

    /**
     * Prompts for selection of a customer account if one is not already.
     * Then calls the callback
     */
    $scope.ensureHaveCustomerAccount = () => new Promise((resolve, reject) => {
        if ($scope.cart.customer.hasAccount()) {
            resolve();
            return;
        }

        // Don't have an account. Show the customer details modal so the user can select one.
        $scope
            .showCustomerDetails({
                langKey: 'SELL.ACCOUNT_SELECTION_REQUIRED',
                type: 'info',
            })
            .then(() => {
                if ($scope.cart.customer.hasAccount()) {
                    resolve();
                } else {
                    reject();
                }
            });
    });

    /**
     * Show the 'hold order' modal.
     */
    $scope.showHoldOrder = () => {
        vueModalFactory
            .open(MODAL_NAMES__EPOS.HOLD_CART)
            .then((result) => {
                $timeout(() => {
                    if (result.held) {
                        // If the cart was successfully stashed clear the current cart and show a confirmation.
                        $scope.clearCart();
                        toastFactory.successTop('ORDERS_ON_HOLD.HELD');
                    }
                });
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    };

    /**
     * Show the 'restore order' modal.
     */
    $scope.showHeldOrders = () => {
        vueModalFactory
            .open(MODAL_NAMES__EPOS.HELD_CARTS)
            .then((result) => {
                $timeout(() => {
                    if (result.restored) {
                        // If the cart was successfully restored show a confirmation.
                        toastFactory.successTop('ORDERS_ON_HOLD.RESTORED');
                    }
                });
            }).catch(() => {
                // The operator closed the modal.
            });
    };

    /**
     * Show the send to kitchen model.
     */
    $scope.showSendToKitchenModal = () => {
        return runPreSendToKitchenValidation()
            .then(() => {
                vueModalFactory
                    .open(MODAL_NAMES__EPOS.SEND_TO_KITCHEN)
                    .then((result) => {
                        $timeout(() => {
                            if (result.sentToKitchen) {
                                // Print the kitchen receipt here from the till if enabled for the device.
                                if (
                                    currentDevice.device.autoPrintKitchenReceipts
                                ) {
                                    kitchenReceiptPrinter.printOrder(
                                        result.createdOrder,
                                        result.restoredKitchenOrder,
                                        false
                                    );
                                }
                                // If the order was successfully sent to the kitchen clear the cart and show
                                // a confirmation.
                                $scope.clearCart();
                                toastFactory.successTop('KITCHEN_MODAL.SENT_TO_KITCHEN');

                                // Is this an order edit?
                                if (result.createdOrder.originalRef) {
                                    // If this order was in the order search cache it will now be out of date
                                    // so clear the cache.
                                    clearOrderSearchCache();
                                }
                            }
                        });
                    }).catch(suppressModalCancelledErrorOrReThrow);
            }).catch((err) => {
                if (!(err instanceof OrderValidationError)) {
                    throw (err);
                }
            });
    };

    /**
     * Show the send to kitchen model.
     */
    $scope.showKitchenOrders = () => {
        vueModalFactory
            .open(MODAL_NAMES__EPOS.KITCHEN_ORDERS)
            .then((result) => {
                $timeout(() => {
                    if (result.restored) {
                        // If the cart was successfully restored show a confirmation.
                        toastFactory.successTop('KITCHEN_MODAL.RESTORED');
                    }

                    if (result.deleteConfirmed) {
                        if (cartService.getRemainingBalance() === 0) {
                            $scope.payButtonClick();
                        } else {
                            let message = 'SELL.CANCEL_TOAST_REFUND';
                            if (cartService.getRemainingBalance() > 0) {
                                message = 'SELL.CANCEL_TOAST_PAYMENT';
                            }
                            toastFactory.success(message, null, {
                                timeOut: 10000,
                            });
                        }
                    }
                });
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    };

    /**
     * @param {boolean} [displayProcessingOverlay]
     */
    $scope.refreshExternalCalculations = (displayProcessingOverlay) => {
        if (displayProcessingOverlay) {
            $rootScope.setRootProcessingMessage('SELL.PROCESSING_CALCULATING_OFFERS');
        }
        let promise = $scope.cart.refreshExternalCalculations(true);
        if (displayProcessingOverlay) {
            promise.finally(() => {
                $rootScope.setRootProcessingMessage(null);
            });
        }
        // Suppress rejections. It will reject if offline but we don't want this to prevent
        // completing a sale.
        return promise.catch((error) => {
            console.warn('Failed to refresh cart calculations', error);
        });
    };

    /**
     * @return {Promise}
     */
    const runPreCheckoutValidation = () => {
        orderValidator.useCheckoutValidators();
        return runPreOrderValidation();
    };

    /**
     * @return {Promise}
     */
    const runPreSendToKitchenValidation = () => {
        orderValidator.useSendToKitchenValidators();
        return runPreOrderValidation();
    };

    /**
     * @return {Promise}
     */
    const runPreOrderValidation = () => orderValidator
        .validate($scope.cart)
        .catch((e) => {
            console.warn('Cannot proceed due to validation failure', e, e.clientMessages, e.cartLines);
            // Something in OrderValidator prevented proceeding to payment.
            // Allow the user to deal with the issue then start validation again as if they had clicked
            // the pay button again.

            if (e instanceof OrderValidationError) {
                switch (e.message) {
                    case OrderValidationError.DELIVERY_ADDRESS_REQUIRED:
                        // Something requires a delivery address but none was given.
                        // Show customer details modal with error message.
                        return $scope.showCustomerDetails(
                            { langKey: 'SELL.DELIVERY_ADDRESS_REQUIRED_ALERT' }
                        ).then(runPreOrderValidation);

                    case OrderValidationError.DEVICE_OFFLINE:
                        // Device must be online for orders but we're offline.
                        // Show an alert.
                        dialogService.alert(
                            'SELL.CONNECTION_REQUIRED_ALERT',
                            {
                                title: 'SELL.CONNECTION_REQUIRED_TITLE',
                                icon: 'fa-wifi-slash',
                            }
                        );
                        throw e; // Re-throw the error so promise is not resolved.

                    case OrderValidationError.CUSTOMER_ACCOUNT_REQUIRED:
                        // Something requires a customer account but none was selected.
                        // Show customer details modal with error message.
                        return $scope.showCustomerDetails(
                            {
                                langKey: 'SELL.ACCOUNT_SELECTION_REQUIRED',
                                type: 'info',
                            }
                        ).then(runPreOrderValidation);

                    case OrderValidationError.FIELD_DATA_INVALID:
                        // Invalid field data was entered.
                        // Show customer detail modal with field errors.
                        return $scope
                            .showCartFields(
                                new UserFacingError(
                                    'Field data invalid',
                                    'CART_FIELDS.FIELD_DATA_INVALID',
                                ),
                                false
                            )
                            .then(runPreOrderValidation);

                    case OrderValidationError.ADDITIONAL_CUSTOM_FIELDS_NOT_PRESENTED:
                        // There are custom fields that have not been presented yet.
                        return $scope
                            .showCartFields(
                                new UserFacingError(
                                    'Available fields changed.',
                                    'CART_FIELDS.FIELDS_CHANGED',

                                ).setLevel(ErrorLevel.INFO),
                                false
                            )
                            .then(runPreOrderValidation);

                    case OrderValidationError.GIFT_AID_UNANSWERED:
                        // There are Gift Aid-able items but the Gift Aid yes/no question has not been asked.
                        // Ask the question.
                        return giftAidPrompter
                            .prompt(cartService)
                            .then(runPreOrderValidation);

                    case OrderValidationError.GIFT_AID_CUSTOMER_INFO_REQUIRED:
                        // Gift Aid was enabled but customer info has not been given.
                        // Show customer details modal with error message.
                        return $scope.showCustomerDetails(
                            { langKey: 'SELL.GIFT_AID_CUSTOMER_INFO_REQUIRED' },
                            true
                        ).then(runPreOrderValidation);

                    case OrderValidationError.ORDER_EDIT_REFUND_DETAILS_NOT_ENTERED:
                        // The operator is using order editing for refunds but has not added either return reasons or
                        // notes.
                        return dialogService.confirm(
                            [
                                'SELL.ORDER_EDIT_REFUND_DETAILS_NOT_ENTERED',
                                'SELL.ORDER_EDIT_REFUND_DETAILS_NOT_ENTERED_LINE_2',
                            ],
                            {
                                cancelLabel: 'SELL.ORDER_EDIT_REFUND_DETAILS_DIALOG_BACK_BTN',
                                cancelColor: 'default',
                                okLabel: 'SELL.ORDER_EDIT_REFUND_DETAILS_DIALOG_CONTINUE_BTN',
                                okColor: 'danger',
                            }
                        ).then((confirmed) => {
                            if (confirmed) {
                                cartService.currentCart.ignoreMissingOrderEditRefundDetails = true;
                                return runPreOrderValidation();
                            }
                            throw e;
                        });

                    case OrderValidationError.STAFF_REF_REQUIRED:
                        // Show staff ref entry screen.
                        return vueModalFactory
                            .open(
                                MODAL_NAMES__EPOS.HOLD_CART,
                                {
                                    icon: 'fa-solid fa-pen-field',
                                    onlyAllowAssignStaffRef: true,
                                    title: 'ORDERS.ADD_STAFF_REF',
                                }
                            )
                            .then(runPreOrderValidation);

                    case OrderValidationError.ADJUSTMENT_VALIDATION_FAILED:
                    case OrderValidationError.DUPLICATE_MEMBER_REFS:
                    case OrderValidationError.DUPLICATE_MEMBERSHIP_REFS:
                    case OrderValidationError.MISSING_MEMBERSHIP_DATA:
                    case OrderValidationError.NEGATIVE_VALUE_ADJUSTMENT_FORBIDDEN:
                    case OrderValidationError.POSITIVE_VALUE_ADJUSTMENT_FORBIDDEN:
                    case OrderValidationError.STOCK_LEVEL_ERROR:
                    default:
                        // Just show an alert with the message.
                        // These errors are defined in the ORDER_VALIDATION section of the lang file, and should match
                        // the cases from OrderValidationError.
                        toastFactory.errorTop(
                            // Set the error title to the known message.
                            'ORDER_VALIDATION.' + e.message,
                            e.clientMessages.length > 0 ? e.clientMessages.join(' ') : null
                        );
                        throw e; // Re-throw the error so promise is not resolved.
                }
            }
            throw e;
        });

    /**
     * @param {CheckoutOptions} [options]
     *
     * @returns {Promise<void>}
     */
    $scope.payButtonClick = async function (options = {}) {
        try {
            await setDeviceTradingSessionIfMissing(currentDevice, tradingSessionManager);
            await runPreCheckoutValidation();
            await $scope.refreshExternalCalculations(true);
            await pay(options);
        } catch (err) {
            const ignorableError = (
                err instanceof OrderValidationError
                || err instanceof ModalCancelledError
                || err === 'cancel'
            );

            if (!ignorableError) {
                throw err;
            }
        }
    };

    $scope.$on(
        EventNames.PAY_REQUESTED,
        /**
         * @param event
         * @param {CheckoutOptions} data
         */
        (event, data) => {
            console.debug('PAY_REQUESTED with the following options', data);
            $scope.payButtonClick(data || {});
        }
    );

    /**
     * The cart "Pay Now" button should be disabled if any of the following conditions is met:
     *
     * - There is a payment in progress.
     * - The cart is empty.
     * - External Calculation is in progress.
     * - We are editing an order and:
     *    - The cart hash has not changed
     *    - None of the cartLines has been adjusted (including fields and line level discounts).
     *    - The remaining balance != 0
     *
     * @return {boolean}
     */
    $scope.payButtonDisabled = function () {
        return !$scope.canPay()
            || cartService.externalCalculationInProgress
            || (cartService.currentCart.isEditingOrder
                && !cartService.currentCart.contentHashChanged()
                && cartService.currentCart.originalOrder.remainingBalance === 0
            );
    };

    /**
     * @param {CheckoutOptions} [options]
     *
     * @returns {Promise<void>}
     */
    const pay = async function (options = {}) {
        $scope.paymentInProgress = true;
        if (!cartService.currentCart.balancePaid) {
            cartService.currentCart.balancePaid = false;
        }

        try {
            const checkoutResult = await checkoutService.checkout(options);

            await $scope.finishOrder(checkoutResult);
        } catch (err) {
            // Log this as a regular log not an error, because it may be normal (such as the user cancelling).
            // Any scary errors should have already been logged as errors in CheckoutService.
            if (!(err instanceof UserCancelledPaymentError)) {
                console.error('Checkout aborted due to exception', err);
            }

            // If an error message was generated, show it to the user.
            if (err instanceof CheckoutError) {
                if (err.message) {
                    toastFactory.errorTop(
                        err.userFacingTitleLangKey,
                        err.message
                    );
                }
            } else {
                if (err.message) {
                    toastFactory.errorTop(
                        'ERROR_TOAST_TITLE',
                        err.message
                    );
                }
            }

            // Delete any reservation created.
            await cartService.deleteReservation();

            // Reset the customer facing screen.
            customerScreenDataService.setStage(CustomerScreenStages.SELL);

            // Reset the state in the current stashed cart.
            orderInProgressStasher.setState('sell');
            await cartService.clearTransactionInProgress();

            if (err.clearCart) {
                await $scope.clearCart();
            }

            if (err instanceof UserCancelledPaymentError) {
                // The user clicked cancel in the payment modal.
                $scope.clearPayment();

                $scope.redirectWhenDone(
                    cartService.currentCart,
                    false,
                    false,
                    true
                );
            } else if (err instanceof CheckoutError && err.redirectForFailure) {
                $scope.redirectWhenDone(cartService.currentCart, false);
            }
        } finally {
            $scope.paymentInProgress = false;
            $rootScope.setRootProcessingMessage();
        }
    };

    /**
     * TODO: Refactor into CheckoutService in https://digitickets.atlassian.net/browse/PROP-1930
     *
     * This is now called by CheckoutService.
     *
     * @returns {Promise<Order>}
     */
    $scope.handlePayingOrderBalanceComplete = function () {
        // Order has been paid for so here we add the payments to the order.

        // Get the original order from the order balance line in the cart.
        let balancePaymentOrder = null;
        if (cartService.currentCart.isEditingOrder) {
            balancePaymentOrder = cartService.currentCart.originalOrder;
        }

        if (!balancePaymentOrder) {
            throw new Error('Failed to find Order Balance line or Adjusted Order after paying order balance.');
        }

        $rootScope.setRootProcessingMessage('SAVING');

        const newPayments = paymentService.cleanPayments(cartService.payments);

        return paymentService.storePaymentsForOrder(
            balancePaymentOrder.ID,
            newPayments
        ).then(() => {
            clearOrderSearchCache();
            const newOrder = cartToOrderMapper.buildOrderFromApiConfirmation(cartService);
            // When paying the balance for an order we are not going to actually create any new orders
            // or adjustment orders. But we still need to know the ID of this order for some things such
            // as sending email receipts.
            // Copy the orderID from the originalOrder to the "newOrder" in this case.
            if (cartService.currentCart.originalOrder?.ID) {
                newOrder.ID = cartService.currentCart.originalOrder?.ID;
            }

            return newOrder;
        }).catch((error) => {
            if (error instanceof PaymentSaveError) {
                balancePaymentOrder.setPayments(
                    // If we are unable to get updated payments after the order adjustment use the payments
                    // from the original order that are already confirmed and saved against the order.
                    error.hydratedConfirmedPayments
                        ? error.hydratedConfirmedPayments
                        : cartService.originalOrder.payments
                );
                // We could think about filtering newPayments to deal with the situation where multiple
                // payments were sent to the API some were successful and some failed so that we remove the
                // newPayments that did actually get saved, but this is harder than you would think and the
                // operator can already resolve things manually.
                error.setNewPayments(newPayments);
            }
            throw error;
        }).finally(() => {
            $rootScope.setRootProcessingMessage(null);
        });
    };

    /**
     * Return all the visible tickets for a specific event.
     *
     * @param {number} eventID
     *
     * @return {Promise<Ticket[]>}
     */
    const getAllTicketsForEvent = async (eventID) => {
        let allTickets = await ticketRepository.findByEvent(userService.getItemBranchID(), eventID);

        /**
         * @type {DeviceGroup|null}
         */
        const deviceGroup = currentDevice.getDevice()?.deviceGroup;
        // Filter tickets by visibility.
        if (deviceGroup?.hasVisibleItems()) {
            const {
                visibleCatIDs,
                visibleEventIDs,
                visibleItemIDs,
            } = deviceGroup;
            allTickets = allTickets.filter(({ catID, eventCatID, ID }) => (
                visibleCatIDs.includes(catID)
                || visibleEventIDs.includes(eventCatID)
                || visibleItemIDs.includes(ID)
            ));
        }

        return allTickets;
    };

    /**
     * Return a unique array of categories that have tickets.
     *
     * @param {Ticket[]} relevantTickets
     *
     * @return {Category[]}
     */
    const getPopulatedEventCategories = (relevantTickets) => {
        let categories = [];
        let uniqueCategoryIDs = [];
        relevantTickets.forEach(function (ticket) {
            if (uniqueCategoryIDs.indexOf(ticket.category.ID) === -1) {
                categories.push(ticket.category);
                uniqueCategoryIDs.push(ticket.category.ID);
            }
        });
        return categories;
    };

    /**
     * @param {number} ticketID
     *
     * @return {Promise}
     */
    const startEventBookingByTicketID = async (ticketID) => {
        /**
         * @type {Ticket}
         */
        const ticket = await itemRepository.getItem(ItemType.TICKET, ticketID);
        if (!ticket.event) {
            throw new Error('Non event-based ticket used in startEventBookingByTicketID.', ticket);
        }

        return startEventBooking(
            ticket.event,
            ticket.category,
            [
                {
                    ticket,
                    qty: 1,
                },
            ]
        );
    };

    /**
     * Add some tickets to the cart by first showing the qty selection modal
     * then showing the session selection modal.
     *
     * @param {SessionEvent} event
     * @param {Category|null} [category]
     * @param {{ticket: Ticket, qty: Number}[]} [initialQuantityData]
     */
    const startEventBooking = async (event, category, initialQuantityData) => {
        const ticketQuantityData = await showTicketPicker(
            event,
            null,
            category,
            initialQuantityData
        );

        if (ticketQuantityData) {
            $scope.lastTicketSelectedCategory = ticketQuantityData.lastTicketSelectedCategory;

            let selectedTickets;
            try {
                selectedTickets = await showSessionModal(ticketQuantityData.tickets, event);
            } catch (e) {
                // Operator cancelled the session selector. Call this function to revert back to the quantity selector.
                startEventBooking(event, category, ticketQuantityData.tickets)
                    .catch(() => {});

                return;
            }
            selectedTickets.forEach(({ ticket, qty, price, session }) => {
                // As session-based pricing rules may have kicked in, create a copy of the Ticket and modify
                // the price of it (so it doesn't affect any references to the same Ticket elsewhere).
                /**
                 * @type {Ticket}
                 */
                const adjustedTicket = cloneShallow(ticket);

                // The price returned from the session picker modal including pricing rules.
                adjustedTicket.price = price;

                $scope.addItem(
                    adjustedTicket,
                    qty,
                    {
                        session,
                        groupBookingReferenceData: ticketQuantityData.groupBookingReferenceData || null,
                    }
                );
            });
        }
    };

    $scope.$on(EventNames.START_EVENT_BOOKING_REQUESTED, (e, data) => {
        startEventBooking(
            data.event,
            data.category,
            data.initialQuantityData || null
        );
    });

    /**
     * @param {SessionEvent} event
     * @param {Session} [session] If we know the session in advance of quantity selection
     * @param {Category} [initialSelectedCategory]
     * @param {{ticket: Ticket, qty: Number}[]} [initialQuantityData]
     * @param {string|null} [groupBookingReference]
     *
     * @return {Promise<{tickets: {ticket: Ticket, qty: Number}[], lastTicketSelectedCategory: Category, groupBookingReference: {string|null}}>}
     */
    const showTicketPicker = async (
        event,
        session = null,
        initialSelectedCategory = null,
        initialQuantityData = null,
        groupBookingReference = null,
    ) => {
        const allTicketsForEvent = await getAllTicketsForEvent(event.ID);
        const allCategoriesForEvent = getPopulatedEventCategories(allTicketsForEvent);

        // Get any cancel return path the user should be sent back to if they cancel.
        // This is pulled out of the sell state so that it only applies one time.
        const cancelTicketPickerReturnPath = await cartService.modifyCurrentCart((currentCart) => {
            return currentCart.sellState.pullCancelTicketPickerReturnPath();
        });

        return vueModalFactory.open(
            MODAL_NAMES__EPOS.TICKET_PICKER,
            {
                allCategoriesForEvent,
                allTicketsForEvent,
                event,
                groupBookingReference,
                initialQuantityData,
                initialSelectedCategory,
                session,
            }
        ).catch((e) => {
            if (e instanceof ModalCancelledError) {
                // The cancel button was pressed on the ticket picker.
                // If there was a return path for this send the user back there.
                if (cancelTicketPickerReturnPath) {
                    navigationService.goToPath(
                        cancelTicketPickerReturnPath.path,
                        null,
                        cancelTicketPickerReturnPath.params
                    );
                }

                return null;
            }

            throw e;
        });
    };

    /**
     * @param {{ticket: Ticket, qty: Number}[]} quantities
     * @param {SessionEvent} event
     * @param {Object<number>} [sessionSpaceAdjustments]
     * @param {boolean} highlightWhenPreferredSessionSelected
     * @param {boolean} autoSubmitPreferredSession
     *
     * @return {Promise<{ticket: Ticket, qty: Number, price: Number, session: Session}[]>}
     */
    const showSessionModal = async (
        quantities,
        event,
        sessionSpaceAdjustments,
        highlightWhenPreferredSessionSelected = false,
        autoSubmitPreferredSession = false
    ) => {
        // Convert quantities to ItemRequest objects.
        // TODO: Cleanup the source of these quantities so that generates ItemRequests.
        // This would be nice with Typescript because it could use an interface and not have to be actual
        // ItemRequest instances.
        const requestedTickets = quantities.map(({ ticket, qty }) => new ItemRequest(ticket, qty));

        return vueModalFactory.open(
            MODAL_NAMES__EPOS.SESSION_PICKER,
            {
                event,
                requestedTickets,
                eventSessionPreferences: cartService.currentCart.eventSessionPreferences,
                sessionSpaceAdjustments: sessionSpaceAdjustments || {},
                highlightWhenPreferredSessionSelected,
                autoSubmitPreferredSession,
            }
        );
    };

    /**
     * Checks that the given CartLine has a branchID the same as the currently selected item branch.
     * If not it displays an error message and returns false.
     *
     * @param {CartLine} cartLine
     */
    const ensureCartLineIsEditableAtCurrentBranch = async (cartLine) => {
        if (!$scope.lineIsEditableAtCurrentBranch(cartLine)) {
            // Try to automatically switch to the appropriate branch.
            if (await autoSwitchToAppropriateBranchForLine(cartLine)) {
                return true;
            }

            // Auto switching failed. Show an error and prevent the edit.
            showChangeItemBranchError(cartLine);
            return false;
        }

        return true;
    };

    /**
     * Check if the given line can be modified with the current item branch selected.
     * This check is because some things, like changing the session for a line, need the sessions or tickets
     * for that specific branch to be loaded.
     * e.g. If you have items/sessions from Branch A visible. You can't change a line from Branch B because you'll
     * have the wrong items/sessions to choose from.
     *
     * @param {CartLine} cartLine
     *
     * @return {boolean}
     */
    $scope.lineIsEditableAtCurrentBranch = (cartLine) => {
        // This is to do with editing lines that contain tickets for a session. So it currently will skip the check
        // if the line has no session.
        if (cartLine.session && cartLine.session.branchID !== userService.getItemBranchID()) {
            return false;
        }

        return true;
    };

    /**
     * @param {CartLine} cartLine
     */
    const showChangeItemBranchError = (cartLine) => {
        toastFactory.error(
            'SELL.CHANGE_BRANCH_TOAST_TITLE',
            langService.getText(
                'SELL.CHANGE_BRANCH_TOAST_MESSAGE',
                { branchName: cartLine.branch.name }
            )
        );
    };

    let previousItemBranchAutoChangedWarningToast = null;
    /**
     * @param {CartLine} cartLine
     */
    const showItemBranchAutoChangedWarning = (cartLine) => {
        // If the branch is auto changed twice rapidly it would be confusing to see two messages.
        // Clear any previous message.
        if (previousItemBranchAutoChangedWarningToast) {
            toastFactory.clear(previousItemBranchAutoChangedWarningToast);
        }

        previousItemBranchAutoChangedWarningToast = toastFactory.info(
            'SELL.BRANCH_AUTO_CHANGED_TOAST_TITLE',
            langService.getText(
                'SELL.BRANCH_AUTO_CHANGED_TOAST_MESSAGE',
                { branchName: cartLine.branch.name }
            ),
            {
                // Twice as long as normal timeout so the user can read it.
                timeOut: 10000,
            },
        );
    };

    /**
     * @param {CartLine} cartLine
     *
     * @return {Promise<boolean>}
     */
    const autoSwitchToAppropriateBranchForLine = async (cartLine) => {
        // Check if the user has access to the new branch.
        const branchFromUser = userService.currentUser.getBranchByID(cartLine.branch.ID);
        if (!branchFromUser) {
            return false;
        }

        await userService.changeItemBranch(branchFromUser);

        showItemBranchAutoChangedWarning(cartLine);

        return true;
    };

    /**
     * @param {CartItemGroup} cartItemGroup
     *
     * @return {Promise}
     */
    $scope.addMoreTicketsForCartItemGroup = async (cartItemGroup) => {
        if (!await ensureCartLineIsEditableAtCurrentBranch(cartItemGroup.items[0])) {
            return Promise.reject();
        }

        return $scope.addTicketsForSession(
            cartItemGroup.event,
            cartItemGroup.session,
            null,
            false,
            cartItemGroup.groupBookingReference
        );
    };

    /**
     * Add more tickets to a session already in the cart. Triggered by the + button on a session group in cart.
     *
     * @param {SessionEvent} event
     * @param {Session} session
     * @param {{ticket: Ticket, qty: Number}[]} [initialQuantityData]
     * @param {boolean} [autoSubmitSession=false] If true, when the flow gets to the session picker it will
     *      automatically select the session from the CartItemGroup if it is still available. If it is not then the
     *      session picker will remain open.
     *      If false (the default) the user will be shown the session picker after selecting their tickets, and the
     *      session from the CartItemGroup will be highlighted and the session picker will remain open.
     * @param {string|null} [groupBookingReference]
     */
    $scope.addTicketsForSession = async (
        event,
        session,
        initialQuantityData = null,
        autoSubmitSession = false,
        groupBookingReference = null,
    ) => {
        let ticketQuantityData = await showTicketPicker(
            event,
            session,
            $scope.lastTicketSelectedCategory || null,
            initialQuantityData,
            groupBookingReference
        );

        if (ticketQuantityData) {
            // Set the preferred session to the selected session. This gets used when the session modal is shown
            // to pre-select a session instead of defaulting to the next session.
            cartService.currentCart.eventSessionPreferences.setPreferredSessionForEvent(
                event.ID,
                session
            );
            $scope.lastTicketSelectedCategory = ticketQuantityData.lastTicketSelectedCategory;
            let selectedTickets;
            try {
                selectedTickets = await showSessionModal(
                    ticketQuantityData.tickets,
                    event,
                    null,
                    true, // highlightWhenPreferredSessionSelected
                    autoSubmitSession, // autoSubmitPreferredSession
                );
            } catch (e) {
                // Operator cancelled the session selector. Call this function to revert back to the quantity selector.
                return $scope.addTicketsForSession(
                    event,
                    session,
                    ticketQuantityData.tickets,
                    autoSubmitSession
                );
            }
            let totalQty = 0;
            selectedTickets.forEach(({ ticket, qty, price, session: ticketSession }) => {
                // As session-based pricing rules may have kicked in, create a copy of the Ticket and modify
                // the price of it (so it doesn't affect any references to the same Ticket elsewhere).
                /**
                 * @type {Ticket}
                 */
                const adjustedTicket = cloneShallow(ticket);

                // The price returned from the session picker modal including pricing rules.
                adjustedTicket.price = price;
                totalQty += qty;

                // TODO: If we create an addItems (plural) method we can move the item added toast logic there,
                // and make it usable for more than just the session tickets from the today screen.
                $scope.addItem(
                    adjustedTicket,
                    qty,
                    {
                        session: ticketSession,
                        groupBookingReferenceData: ticketQuantityData.groupBookingReferenceData || null,
                    }
                );
                combineDuplicateLines($scope.cart.currentCart);
            });

            // Set the last added item qty to the total qty of items added in this flow.
            // This will be used if an "item added" toast is shown.
            await cartService.modifyCurrentCart((currentCart) => {
                currentCart.sellState.lastAddedItemQty = totalQty;
            });
        }

        return Promise.resolve();
    };

    $scope.suppressModalCancelledError = suppressModalCancelledErrorOrReThrow;

    /**
     * Change the selected session for every CartItem in the given CartItemGroup (already in the cart).
     *
     * @param {CartItemGroup} cartItemGroup
     */
    $scope.changeCartItemGroupSession = async (cartItemGroup) => {
        if (cartItemGroup.containsUneditableLines()) {
            return;
        }

        if (!await ensureCartLineIsEditableAtCurrentBranch(cartItemGroup.items[0])) {
            return;
        }

        /**
         * Object to pass to session space calculator's sessionSpaceAdjustments containing the total
         * spaces for all the tickets for a given session.
         *
         * @type {Object<number>}
         */
        let sessionSpaceAdjustments = countSessionSpacesInGroup(cartItemGroup);

        /**
         * List of selected tickets from those in the cartItemGroup.
         *
         * @type {{ticket: Ticket, qty: Number}[]|*}
         */
        let existingTickets = getTicketQtysInGroup(cartItemGroup);

        // Make it so the currently chosen session is pre-selected in the session modal.
        cartService.currentCart.eventSessionPreferences.setPreferredSessionForEvent(
            cartItemGroup.event.ID,
            cartItemGroup.session
        );

        let selectedTickets = await showSessionModal(
            existingTickets,
            cartItemGroup.event,
            // Add the spaces used by this group back to the number of available spaces.
            sessionSpaceAdjustments
        ).catch(suppressModalCancelledErrorOrReThrow);

        if (selectedTickets) {
            // selectedTickets is an array in the same order as the existingTickets we passed in. Therefore, we can
            // use that same order to get the lineNumber from existingTickets and update the relevant cart lines.
            selectedTickets.forEach(({ price, session }, index) => {
                let { lineNumber } = existingTickets[index];
                let cartLine = cartService.currentCart.getLineByNumber(lineNumber);
                cartLine.session = session;
                // Pricing rules may have applied so update the price too.
                cartLine.setPrice(price);

                // If the existing line has gift aid applied or a non gift aid donation then (re)adjust the price.
                if (cartLine.giftAid || $scope.cart.giftAidPrices) {
                    cartLine.useGiftAidRateDonation();
                }

                // After changing the session set that new session as the preferred one for this event.
                cartService.currentCart.eventSessionPreferences.setPreferredSessionForEvent(
                    session.eventID,
                    session
                );
            });

            cartService.recalculateAfterModifyingCartContents().then(() => {});
        }
    };

    /**
     * @param {CartLine} cartLine
     * @param {string|null} [initialTabName]
     * @param {boolean} [triggeredByItemButton]
     * @param {string} [cancelLabel]
     */
    $scope.showItemEditModal = async (
        cartLine,
        initialTabName = null,
        triggeredByItemButton = false,
        cancelLabel = 'CANCEL',
    ) => {
        if (!await ensureCartLineIsEditableAtCurrentBranch(cartLine)) {
            return;
        }

        const props = {
            cartLine,
            initialTabName,
            triggeredByItemButton,
            cancelLabel,
        };

        if (triggeredByItemButton && initialTabName === CartLineDetailsTabNameEnum.FIELDS) {
            // Only show the options tab.
            props.onlyFields = true;
        } else if (triggeredByItemButton && initialTabName === CartLineDetailsTabNameEnum.RETURNS) {
            // Only show the returns tab.
            props.onlyReturns = true;
        }

        vueModalFactory
            .open(
                MODAL_NAMES__EPOS.CART_LINE_DETAILS,
                props,
            )
            .then(async () => {
                // Recalculate this cart line so that changes to chargeable product options
                // are reflected in the price.
                await cartLine.calculate(false);

                // Merge any now-duplicate lines in the cart.
                combineDuplicateLines(cartService.currentCart);

                // Recalculate the whole cart.
                await cartService.recalculateAfterModifyingCartContents();

                // Recalculate any automatic offers.
                await cartService.refreshExternalCalculations();
            })
            .catch((err) => {
                if (err instanceof ModalCancelledError) {
                    if (triggeredByItemButton) {
                        // Remove the last item added to the cart if the user
                        // cancelled the options modal.
                        cartService.remove(cartLine);
                    }
                } else {
                    throw err;
                }
            })
            .finally(() => {
                updateAngularUi();
            });
    };

    /**
     * @param {Cart} cart
     * @param {CartLine} cartItem
     */
    $scope.showStockLevelModal = function (cart, cartItem) {
        const message = generateStockLevelMessage(cart, cartItem);

        dialogService
            .confirm(
                '',
                {
                    customHtml: message,
                    title: 'ORDER_DETAILS.STOCK_LEVEL_MODAL_TITLE',
                    cancelLabel: 'ORDER_DETAILS.STOCK_LEVEL_REJECT_PURCHASE',
                    okLabel: 'ORDER_DETAILS.STOCK_LEVEL_AUTHORISE_PURCHASE',
                }
            )
            .then((confirmed) => {
                $timeout(() => {
                    cart.cartProductStockLevels.setDisableStockValidation(
                        cartItem.branch.ID,
                        cartItem.item.ID,
                        confirmed
                    );
                    cartService._triggerOnChange();
                });
            });
    };

    /**
     * Display the popup to add/edit gift voucher data.
     * In $scope because it's opened when clicking a gift voucher in the cart.
     *
     * @param {string} action
     * @param {CartLine} cartItem
     *
     * @return {Promise<{price: Number, soldGiftVoucherData: SoldGiftVoucherData}>}
     */
    $scope.showSoldGiftVoucherDataModal = (action, cartItem) => {
        return vueModalFactory
            .open(
                MODAL_NAMES__EPOS.SOLD_GIFT_VOUCHER_DATA,
                {
                    action,
                    giftVoucher: cartItem.getItem(),
                    initialPrice: cartItem.getPrice(),
                    data: cloneDeep(cartItem.soldGiftVoucherData),
                }
            )
            .then(
                /**
                 * @param {{price: Number, soldGiftVoucherData:SoldGiftVoucherData}} response
                 * @return {{price: Number, soldGiftVoucherData:SoldGiftVoucherData}}
                 */
                (response) => {
                    $timeout(() => {
                        cartItem.customPrice = response.price;
                        cartItem.setPrice(response.price);
                        cartItem.soldGiftVoucherData = response.soldGiftVoucherData;
                    });

                    return response;
                }
            );
    };

    /**
     * @param {CartLine} cartLine
     *
     * @return {Promise<unknown>}
     */
    const showPriceEditModal = async (cartLine) => {
        return vueModalFactory
            .open(
                MODAL_NAMES__EPOS.PRICE_EDIT,
                {
                    cartLine,
                }
            )
            .then(({ priceChanged, newPrice }) => {
                if (priceChanged) {
                    logger.log(
                        'Price adjusted',
                        {
                            itemID: cartLine.getID(),
                            newPrice,
                        }
                    );
                }
            });
    };

    /**
     * A membership plan is being added to the cart. Display the modal to enter info for this new membership.
     * This adds a new MembershipDataset to the line.
     *
     * @param {CartLine} cartItem
     *
     * @return {Promise}
     */
    $scope.displayNewMembershipDatasetModal = async (cartItem) => $scope.ensureHaveCustomerAccount()
        .then(() => {
            // Clone the array of membershipDatasets already on this cartItem.
            // This is so if the modal is cancelled the cartItem's array is not modified.
            // (The modal itself will handle cloning the object within the array so they are not modified
            // during editing)
            let membershipDatasets = cartItem.membershipDatasets.slice(0);

            // Add one more dataset to the datasets for the new instance.
            membershipDatasets.push(
                new MembershipDataset(cartItem.getItem())
            );

            return new Promise((resolve, reject) => {
                // Show the membership datasets modal to allow entering the new plan data.
                membershipManagementService.editDatasets(
                    'add-new',
                    cartItem.getItem(),
                    membershipDatasets,
                    $scope.cart.customer.account,
                    function (filledMembershipDatasets) {
                        if (filledMembershipDatasets) {
                            // New instances don't exist yet so we return the membershipDatasets and attach them to the
                            // instances once they have been created.
                            resolve(filledMembershipDatasets);
                        } else {
                            reject();
                        }
                    }
                );
            });
        });

    /**
     * Edit the datasets for a cart line.
     *
     * @param {CartLine} cartItem
     * @param $event
     */
    $scope.editMembershipPlan = function editMembershipPlan(cartItem, $event) {
        $event.preventDefault();
        $event.stopPropagation();

        membershipManagementService.editDatasets(
            'edit-new',
            cartItem.getItem(),
            cartItem.membershipDatasets,
            $scope.cart.customer.account,
            async function (filledMembershipDatasets) {
                if (filledMembershipDatasets) {
                    await cartService.modifyCurrentCart(() => {
                        cartItem.setMembershipDatasets(filledMembershipDatasets);
                    });
                }
            }
        );
    };

    /**
     * @returns {Promise}
     */
    $scope.showCartOffers = () => {
        return vueModalFactory
            .open(
                MODAL_NAMES__EPOS.CART_OFFERS,
                {
                    cart: cartService.currentCart,
                }
            )
            .then(async (result) => {
                // The modal does not modify the cart itself, it just returns the data. Save the data here.
                logger.log('Selected cart offer data', result);
                $scope.cart.setManualOfferData(result)
                    .then((calculationResult) => {
                        if (calculationResult.errors && calculationResult.errors.length > 0) {
                            calculationResult.errors.forEach((error) => {
                                toastFactory.warningTop(null, error);
                            });
                        }
                    });
                cartService.recalculateAfterModifyingCartContents().then(() => {});
            })
            .catch(suppressModalCancelledErrorOrReThrow)
            .finally(updateAngularUi);
    };

    $scope.showCustomerDetailsForGiftAid = () => {
        return $scope.showCustomerDetails(
            { langKey: 'SELL.GIFT_AID_CUSTOMER_INFO_REQUIRED' },
            true
        );
    };

    /**
     * @param {{langKey: string, type?: string}|null} errorMsg
     * @param {boolean} validateForGiftAid
     *
     * @return {Promise<{customer: Customer}>}
     */
    $scope.showCustomerDetails = function (errorMsg, validateForGiftAid = false) {
        return modalFactory.open(
            'customer-details',
            CustomerDetailsCtrl,
            'partials/modals/customerDetails.html',
            {
                // Provide a clone of the current customer so it does not get modified until
                // OK is pressed.
                customer: () => cloneDeep($scope.cart.customer),
                errorMsg: () => errorMsg,
                validateForGiftAid: () => validateForGiftAid,
                paymentInProgress: () => false,
            }
        ).then((result) => {
            // Use the returned customer.
            $scope.cart.customer = result.customer;

            cartService.recalculateAfterModifyingCartContents().then(() => {});
            cartService.refreshExternalCalculations();

            return result;
        });
    };

    /**
     * @param {UserFacingError} [initialError]
     * @param {boolean} [suppressCancel] If false this will re-throw the error if the modal is cancelled.
     *
     * @returns {Promise}
     */
    $scope.showCartFields = (initialError = null, suppressCancel = true) => {
        return vueModalFactory
            .open(
                MODAL_NAMES__EPOS.CART_FIELDS,
                {
                    cart: cartService.currentCart,
                    initialError,
                }
            )
            .then(({ fieldValues }) => {
                // The modal does not modify the cart itself, it just returns the data. Save the data here.
                setFieldValuesOnCart(
                    fieldValues,
                    $scope.cart.currentCart,
                    FieldInstanceFilter.NON_ASK_EARLY_FIELDS_ONLY,
                    true // Clear existing values for instances and only use the new values.
                );

                cartService.recalculateAfterModifyingCartContents().then(() => {});
            })
            .catch((err) => {
                if (suppressCancel) {
                    suppressModalCancelledErrorOrReThrow(err);
                } else {
                    throw err;
                }
            })
            .finally(updateAngularUi);
    };

    /**
     * @returns {Promise}
     */
    $scope.showOrderNotes = () => {
        return vueModalFactory
            .open(
                MODAL_NAMES__EPOS.ORDER_NOTES,
                {
                    orderNotes: cartService.notes,
                }
            )
            .then((result) => {
                // The modal does not modify the cart itself, it just returns the data. Save the data here.
                cartService.notes = result;
            })
            .catch(suppressModalCancelledErrorOrReThrow)
            .finally(updateAngularUi);
    };

    const showMixedPaymentPatternError = () => {
        toastFactory.warningTop('SELL.CANNOT_MIX_PAYMENT_PATTERNS');
    };

    $scope.init = async function init() {
        /**
         * Count how many manual cart offers there are to determine if we should show the offers button.
         * (If not already cached.)
         */
        if (window.rootVueStore.state.cartOffers.manualOrderOffersCount === null) {
            offerRepository
                .countAllManualOrderOffers()
                .then((count) => {
                    // Store it in Vuex for the next sell screen load.
                    window.rootVueStore.commit('cartOffers/setManualOrderOffersCount', count);
                    // Toggle the offers button.
                    $scope.showOffersButton = count > 0;
                    updateAngularUi();
                });
        }

        /**
         * Count how many non-ask-early custom fields there are to determine if we should show the fields button.
         * (If not already cached.)
         */
        if (window.rootVueStore.state.sell.fieldsCount === null) {
            fieldRepository
                .countAllActiveNonAskEarlyFields()
                .then((count) => {
                    // Store it in Vuex for the next sell screen load.
                    window.rootVueStore.commit('sell/setFieldsCount', count);
                    // Toggle the fields button.
                    $scope.showFieldsButton = count > 0;
                    updateAngularUi();
                });
        }

        const orderInProgress = userService.currentUserSession.orderInProgress;

        if (orderInProgress) {
            // On load, see if there was a cart/order in progress, possibly with some payments already taken.

            // Small delay so things can load up before we start showing modals.
            await timeoutPromise(500);

            const hasOrderErrors = orderInProgress?.stashedCart?.currentCart?.hasUnresolvedOrderSaveErrors();
            if (hasOrderErrors) {
                console.log('Restored unresolved order errors.', cartService.currentCart.getUnresolvedOrderSaveErrors());
            }
            const hasPaymentErrors = orderInProgress.stashedCart?.currentCart?.hasUnresolvedPaymentSaveErrors();
            if (hasPaymentErrors) {
                console.log('Restored unresolved payment errors.', cartService.currentCart.getUnresolvedPaymentSaveErrors());
            }

            if (hasOrderErrors || hasPaymentErrors) {
                // Restore any stashed order/payment errors after refreshing the app.
                await checkoutService.displayErrorModals(
                    cartService.currentCart,
                    cartService.currentCart?.checkoutResult?.order
                );
            } else if (orderInProgress.state === 'pay') {
                // Check if we were in the middle of payment and need to open the payment modal.
                console.log('Order In Progress was at payment stage - going to pay.');
                // Open the payment window.
                // This skips the call to calculate offers you get when clicking the pay button.
                pay();
            }
        }

        const autoAddSessionID = cartService.currentCart.sellState.pullAutoAddSessionID();
        const autoAddProductItemID = cartService.currentCart.sellState.pullAutoAddProductItemID();
        const autoAddEventTicketID = cartService.currentCart.sellState.pullAutoAddEventTicketID();

        if (autoAddSessionID || autoAddEventTicketID || autoAddProductItemID) {
            // Save the cart now that these have been pulled from the sell state.
            await cartService.recalculateAfterModifyingCartContents();
        }

        if (autoAddProductItemID) {
            const getProductItem = await itemRepository.getItem(ItemType.PRODUCT, autoAddProductItemID);
            vueModalFactory.closeAllOfType(MODAL_NAMES__EPOS.SEARCH);
            await $scope.addItem(
                getProductItem,
                1,
                null,
                false
            );
            toastFactory.success(
                'SELL.ITEM_ADDED_TOAST_TITLE',
                new LangString('SELL.ITEM_ADDED_TOAST_MESSAGE', { qty: 1 }),
            );
        }

        if (autoAddSessionID) {
            // If a sessionID was provided in the query string, pop up the ticket picker for that session now.
            await $scope.showTickerPickerAndAutoSubmitSessionByID(autoAddSessionID)
                .catch(suppressModalCancelledErrorOrReThrow);

            const showItemAddedToast = cartService.currentCart.sellState.pullShowItemAddedToast();
            const lastAddedItemQty = cartService.currentCart.sellState.pullLastAddedItemQty();
            const addItemReturnPath = cartService.currentCart.sellState.pullAddItemReturnPath();

            // If a success toast was requested, display it.
            if (showItemAddedToast && lastAddedItemQty) {
                // Display a toast to say the item was added to the cart.
                toastFactory.success(
                    'SELL.ITEM_ADDED_TOAST_TITLE',
                    new LangString('SELL.ITEM_ADDED_TOAST_MESSAGE', { qty: lastAddedItemQty }),
                );
            }

            // If a return path after adding an item was specified, go to it.
            if (addItemReturnPath) {
                navigationService.goToAppPath(addItemReturnPath);
                return;
            }
        } else if (autoAddEventTicketID) {
            // If a addEventTicketID was provided in the query string, start the event ticket booking flow
            // for that session's event if it's an event-based ticket. Or just add it to the cart if it's not.
            startEventBookingByTicketID(autoAddEventTicketID)
                .catch(suppressModalCancelledErrorOrReThrow);
        }

        if ($scope.getCurrentSellState().payNow === true && !$scope.cart.isEmpty()) {
            $scope.payButtonClick();
        }
    };

    $timeout($scope.init, 50);

    /**
     * Open the ticket picker with a specific session selected.
     *
     * @param {number} sessionID
     */
    $scope.showTickerPickerAndAutoSubmitSessionByID = async (sessionID) => {
        // We need to load the Session to find the eventID for that session.
        /**
         * @type {Session}
         */
        const session = await sessionRepository.find(sessionID);

        // We need to load the Event to pass to the method.
        /**
         * @type {SessionEvent}
         */
        const event = await sessionEventRepository.find(session.eventID);

        return $scope.addTicketsForSession(event, session, [], true);
    };

    /**
     * Functions for formatting dates/times in the view.
     * (Moved out of CartLine model.)
     */

    /**
     * @param {Date} date
     *
     * @return {string}
     */
    $scope.formatDate = (date) => {
        if (toYMD(date) === toYMD(new Date())) {
            return 'Today';
        }

        return moment(date).format(DateFormat.DATE);
    };

    /**
     * @param {Date} date
     *
     * @return {string}
     */
    $scope.formatTime = (date) => {
        if (date instanceof Date) {
            return moment(date).format(DateFormat.TIME);
        }

        return '00:00';
    };

    /**
     * Functions for managing the visibility and disabled state of items in the view.
     * Moved here because the number of conditions of the ng-if and ng-disabled attributes were becoming too complicated
     * to deal with.
     */

    /**
     * Is the delete (bin) button disabled?
     *
     * @param {CartLine} cartLine
     *
     * @return {boolean}
     */
    $scope.isDeleteButtonDisabled = (cartLine) => {
        // The delete button will be disabled if:
        return (
            // The balance of the cart has already been paid and we're waiting to move on
            $scope.cart.isBalancePaid()
            // The line is marked as non-editable
            || !cartLine.isEditable()
            // The line is marked as non-removable
            || !cartLine.canRemove
            // This is a line from an existing order. In this case you must change the qty to 0 to remove the line.
            || ($scope.cart.currentCart.isEditingOrder && cartLine.ordersItemsID)
            // The sell state says we can't remove items.
            || $scope.getCurrentSellState().preventRemoval
        );
    };

    /**
     * Is the decrease qty (minus) button) visible?
     *
     * @param {CartLine} cartLine
     *
     * @return {boolean}
     */
    $scope.isDecreaseQtyButtonVisible = (cartLine) => {
        return cartLine.canAdjustQuantity !== false
            && $scope.getCurrentSellState().preventQtyAdjustment !== true;
    };

    /**
     * Is the decrease qty (minus) button) disabled?
     *
     * @param {CartLine} cartLine
     *
     * @return {boolean}
     */
    $scope.isDecreaseQtyButtonDisabled = (cartLine) => {
        return (
            $scope.cart.isBalancePaid()
            || !$scope.canPay()
            || !cartLine.isEditable()
            || (cartLine.minimumQuantity !== null && cartLine.quantity <= cartLine.minimumQuantity)
            || (
                // When editing an existing order to remove an existing line the quantity is set to 0. The quantity
                // cannot be < 0 and if we are adding a new line in the process of editing the order then the quantity
                // cannot be 0 either.
                $scope.cart.currentCart.isEditingOrder && (
                    cartLine.quantity === 0 || (cartLine.quantity === 1 && cartLine.qtyBeforeAdjustment === 0)
                )
            )
        );
    };

    /**
     * Is the manual qty input field visible in a new order?
     *
     * @param {CartLine} cartItem
     *
     * @return {boolean}
     */
    $scope.isQtyColumnVisibleForNewOrder = (cartItem) => {
        return (
            cartItem.canAdjustQuantity !== false
            && $scope.getCurrentSellState().preventQtyAdjustment !== true
            && !$scope.cart.currentCart.isEditingOrder
        );
    };

    /**
     * Is the manual qty input field disabled in a new order?
     *
     * @param {CartLine} cartItem
     *
     * @return {boolean}
     */
    $scope.isQtyColumnDisabledForNewOrder = (cartItem) => {
        return (
            cartItem.isMembershipPlan()
            || $scope.cart.isBalancePaid()
            || !$scope.canPay()
            || !cartItem.isEditable()
        );
    };

    /**
     * Is the manual qty input field visible in a new order?
     *
     * @param {CartLine} cartItem
     *
     * @return {boolean}
     */
    $scope.isQtyColumnVisibleWhenEditingOrder = (cartItem) => {
        return (
            cartItem.canAdjustQuantity !== false
            && $scope.getCurrentSellState().preventQtyAdjustment !== true
            && $scope.cart.currentCart.isEditingOrder
        );
    };

    /**
     * Is the manual qty input field disabled in a new order?
     *
     * @param {CartLine} cartItem
     *
     * @return {boolean}
     */
    $scope.isQtyColumnDisabledWhenEditingOrder = (cartItem) => {
        return (
            cartItem.isMembershipPlan()
            || $scope.cart.isBalancePaid()
            || !$scope.canPay()
            || !cartItem.isEditable()
        );
    };

    /**
     * Is the increase qty (plus) button visible?
     *
     * @param {CartLine} cartItem
     *
     * @return {boolean}
     */
    $scope.isIncreaseQtyButtonVisible = (cartItem) => {
        return cartItem.canAdjustQuantity !== false && $scope.getCurrentSellState().preventQtyAdjustment !== true;
    };

    /**
     * Is the increase qty (plus) button disabled?
     *
     * @param {CartLine} cartItem
     *
     * @return {boolean}
     */
    $scope.isIncreaseQtyButtonDisabled = (cartItem) => {
        return $scope.cart.isBalancePaid() || !$scope.canPay() || !cartItem.isEditable();
    };

    /**
     * Is the edit (pencil) button disabled?
     *
     * @param {CartLine} cartItem
     *
     * @return {boolean}
     */
    $scope.isEditButtonDisabled = (cartItem) => {
        return $scope.cart.isBalancePaid()
            || !$scope.canPay()
            || !cartItem.isEditable();
    };

    /**
     * Currently unused method but may be useful in the future.
     *
     * Remove 1 instance from a line and create a new minimal line with qty 1.
     * The copy deliberately does not copy over additional data like field data.
     *
     * @param {CartLine} cartItem
     */
    $scope.splitLineWithCustomFields = (cartItem) => {
        if (cartItem.itemInstances.length > 1) {
            cartItem.adjustQuantity(-1);
            let extraInfo = {};
            if (cartItem.session) {
                extraInfo.session = cartItem.session;
            }
            $scope.addItem(
                cartItem.item,
                1,
                extraInfo,
                true
            );
        }
    };

    /**
     * From PROP-152 the current SellState will come from the current cart. But for now it lives here in the controller.
     * Use this method to get it so lots of places don't need updating when that changes.
     *
     * @return {SellState}
     */
    $scope.getCurrentSellState = () => {
        // From PROP-152 onwards any new carts will have a SellState present on them.
        // But there may be stashed carts, or carts in progress while the update is released.
        // Make sure these don't break if there is no sellState on the current cart.
        if (!cartService.currentCart.sellState) {
            cartService.currentCart.sellState = new SellState();
        }

        return cartService.currentCart.sellState;
    };

    $scope.showBranchNamesOnItems = () => {
        return cartService.currentCart.isCrossBranch(userService.getOrderBranchID());
    };

    /**
     * @return {boolean}
     */
    $scope.isTakingBalancePayment = () => {
        return cartService.isTakingBalancePayment();
    };

    /**
     * @param {CartItemGroup} cartItemGroup
     *
     * @returns {Promise}
     */
    $scope.editCartItemGroupGroupBookingReference = async (cartItemGroup) => {
        vueModalFactory
            .open(
                MODAL_NAMES__EPOS.GROUP_BOOKING_REFERENCE,
                {
                    initialValue: cartItemGroup.groupBookingReference,

                    // Allow the user to select "no reference" to clear an existing value.
                    allowNone: true,
                }
            )
            .then(async (result) => {
                await handleGroupBookingReferenceEdit(cartItemGroup, result);
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    };
};
